import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Search } from "lucide-react";
import { Eyebrow } from "@/components/common/Section";
import { Reveal } from "@/components/common/Reveal";
import { EdmontonMap } from "@/components/relocating/EdmontonMap";
import {
  EDMONTON_COMMUNITIES,
  BEST_FOR_TAGS,
} from "@/data/communities";
import { listProperties, money } from "@/lib/properties";

/* --------------------------------------------------------------------
 * Calgary — placeholder list until we build out real community data +
 * a Calgary map layer. Copy is honest: "we're expanding here".
 * ------------------------------------------------------------------ */
const CALGARY_COMMUNITIES = [
  {
    slug: "bridgeland",
    name: "Bridgeland",
    city: "calgary",
    vibe:
      "Bridge-close-to-downtown neighbourhood with the best neighbourhood coffee culture in Calgary.",
    rent: "$1,500–2,200",
    bestFor: [BEST_FOR_TAGS.young, BEST_FOR_TAGS.food, BEST_FOR_TAGS.transit],
  },
  {
    slug: "inglewood",
    name: "Inglewood",
    city: "calgary",
    vibe:
      "Calgary's oldest main street — indie shops, breweries, and the Bird Sanctuary.",
    rent: "$1,600–2,400",
    bestFor: [BEST_FOR_TAGS.creatives, BEST_FOR_TAGS.arts, BEST_FOR_TAGS.food],
  },
  {
    slug: "kensington",
    name: "Kensington",
    city: "calgary",
    vibe:
      "Walking-village pocket north of the Bow — cafés, restaurants, C-Train two stops from downtown.",
    rent: "$1,600–2,300",
    bestFor: [BEST_FOR_TAGS.young, BEST_FOR_TAGS.transit, BEST_FOR_TAGS.food],
  },
  {
    slug: "mission",
    name: "Mission",
    city: "calgary",
    vibe:
      "Elbow-side, patio-heavy neighbourhood tucked between downtown and Stampede Park.",
    rent: "$1,700–2,500",
    bestFor: [BEST_FOR_TAGS.young, BEST_FOR_TAGS.food, BEST_FOR_TAGS.couples],
  },
];

const PROPERTY_TYPES = [
  { key: "all", label: "All homes" },
  { key: "1", label: "1 bed" },
  { key: "2", label: "2 bed" },
  { key: "3", label: "3+ bed" },
  { key: "townhome", label: "Townhome" },
];

export default function Residences() {
  const [params] = useSearchParams();
  const initial = params.get("city") === "calgary" ? "calgary" : "edmonton";
  const [city, setCity] = useState(initial);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [hovered, setHovered] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sync city when the query param changes (deep-link support).
  useEffect(() => {
    const p = params.get("city");
    if (p === "edmonton" || p === "calgary") setCity(p);
  }, [params]);

  useEffect(() => {
    let cancel = false;
    listProperties()
      .then((data) => !cancel && setProperties(data))
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  // Group properties by community_slug for count + min-price per community.
  const byCommunity = useMemo(() => {
    const map = new Map();
    for (const p of properties) {
      if (p.city?.toLowerCase() !== city) continue;
      const slug = p.community_slug || slugForCommunity(p.community);
      if (!slug) continue;
      const prevs = map.get(slug) || [];
      map.set(slug, [...prevs, p]);
    }
    return map;
  }, [properties, city]);

  const communities = city === "edmonton" ? EDMONTON_COMMUNITIES : CALGARY_COMMUNITIES;

  // Filters: text search on name+vibe, type = beds filter across the
  // community's homes. Type filter only narrows when properties exist.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return communities.filter((c) => {
      if (term) {
        const hay = `${c.name} ${c.vibe} ${(c.bestFor || []).join(" ")}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (type !== "all") {
        const homes = byCommunity.get(c.slug) || [];
        // If we have real homes here, the type filter must match at least one
        // unit-type of one of them. If no homes yet, keep it visible so the
        // user still sees the community exists.
        if (homes.length > 0) {
          const anyMatch = homes.some((p) =>
            (p.unit_types || []).some((u) => matchesType(u, type))
          );
          if (!anyMatch) return false;
        }
      }
      return true;
    });
  }, [communities, q, type, byCommunity]);

  const filteredSlugs = useMemo(
    () => new Set(filtered.map((c) => c.slug)),
    [filtered]
  );

  return (
    <main data-testid="residences-page" style={{ backgroundColor: "#F8F5EF" }}>
      {/* HEADER */}
      <section className="relative" style={{ paddingTop: 132, paddingBottom: 36 }}>
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <Eyebrow color="#8A6220">Residences · Homes for rent</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="font-display mt-6 max-w-[22ch]"
              style={{
                fontSize: "clamp(48px,7vw,88px)",
                color: "#201E1A",
                lineHeight: 1,
                letterSpacing: "-0.015em",
              }}
            >
              Rent by{" "}
              <span className="italic" style={{ color: "#8A6220" }}>
                neighbourhood.
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="body-measure mt-6 text-[17px]"
              style={{ color: "rgba(32,30,26,0.72)" }}
            >
              Pick the pocket of the city that fits how you actually live —
              coffee, commute, weekend. Then see the homes we have for rent
              there. Or don&rsquo;t — the community page alone is worth the
              scroll.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CITY TOGGLE + FILTERS */}
      <section className="sticky top-[76px] z-20" style={{ backgroundColor: "#F8F5EF" }}>
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div
            className="flex flex-col gap-4 border-b py-4 md:flex-row md:items-center md:justify-between"
            style={{ borderColor: "rgba(32,30,26,0.12)" }}
          >
            <div className="flex items-center gap-2" role="tablist" data-testid="city-toggle">
              {["edmonton", "calgary"].map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={city === c}
                  onClick={() => setCity(c)}
                  className="label-caps rounded-full px-4 py-2 text-[10px] transition-colors"
                  style={{
                    backgroundColor: city === c ? "#14231B" : "transparent",
                    color: city === c ? "#F8F5EF" : "#201E1A",
                    border: "1px solid rgba(32,30,26,0.2)",
                  }}
                  data-testid={`city-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center md:justify-end">
              {/* Type filter */}
              <div className="flex flex-wrap gap-2" role="tablist" data-testid="type-filters">
                {PROPERTY_TYPES.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setType(t.key)}
                    className="label-caps rounded-full px-3 py-1.5 text-[10px] transition-colors"
                    style={{
                      backgroundColor:
                        type === t.key ? "rgba(200,154,78,0.16)" : "transparent",
                      color: type === t.key ? "#8A6220" : "rgba(32,30,26,0.65)",
                      border:
                        type === t.key
                          ? "1px solid rgba(200,154,78,0.55)"
                          : "1px solid rgba(32,30,26,0.15)",
                    }}
                    data-testid={`type-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {/* Location search */}
              <label
                className="flex items-center gap-2 rounded-full px-3 py-2"
                style={{ border: "1px solid rgba(32,30,26,0.15)" }}
              >
                <Search size={14} color="rgba(32,30,26,0.5)" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter by location…"
                  className="w-[180px] bg-transparent text-[13px] outline-none"
                  data-testid="location-search"
                />
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT EXPLORER */}
      <section className="mx-auto max-w-[1440px] px-6 pb-24 pt-8 md:px-10">
        {city === "edmonton" ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-[42%_58%] md:gap-10 md:items-start">
            {/* LIST */}
            <div className="flex flex-col gap-3 md:h-[85vh] md:overflow-y-auto md:pr-4">
              <AnimatePresence initial={false}>
                {filtered.map((c) => (
                  <CommunityRow
                    key={c.slug}
                    c={c}
                    listings={byCommunity.get(c.slug) || []}
                    hovered={hovered === c.slug}
                    onHover={() => setHovered(c.slug)}
                    onLeave={() => setHovered(null)}
                  />
                ))}
              </AnimatePresence>
              {filtered.length === 0 && (
                <p className="mt-8 text-center text-[14px] opacity-60">
                  No neighbourhoods match. Clear filters?
                </p>
              )}
            </div>
            {/* MAP */}
            <div className="md:sticky md:top-[188px]">
              <div className="h-[52vh] md:h-[85vh]">
                <EdmontonMap
                  hoveredSlug={hovered}
                  filteredSlugs={filteredSlugs}
                  onHover={setHovered}
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div
              className="mb-8 rounded-2xl p-4 text-[13px]"
              style={{
                backgroundColor: "rgba(200,154,78,0.12)",
                color: "#8A6220",
                border: "1px solid rgba(200,154,78,0.4)",
              }}
            >
              <span className="label-caps mr-2 text-[10px]">Calgary map</span>
              coming soon — we&rsquo;re building the same explorer view for
              our four Calgary pockets. Below is the list version.
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {filtered.map((c) => (
                <CommunityRow
                  key={c.slug}
                  c={c}
                  listings={byCommunity.get(c.slug) || []}
                  hovered={hovered === c.slug}
                  onHover={() => setHovered(c.slug)}
                  onLeave={() => setHovered(null)}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

/* One community row: hover-linked to map, with live rental count + inline
 * listing preview when we have homes there. */
const CommunityRow = ({ c, listings, hovered, onHover, onLeave }) => {
  const count = listings.length;
  const minRent = countMinRent(listings);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      className="group block rounded-2xl p-5 transition-colors"
      style={{
        backgroundColor: hovered ? "#EDE9E0" : "transparent",
        border: "1px solid rgba(32,30,26,0.08)",
      }}
      data-testid={`community-row-${c.slug}`}
    >
      <Link to={`/neighbourhoods/${c.slug}`} className="block">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <h3
                className="font-display truncate"
                style={{ fontSize: 22, color: "#201E1A", lineHeight: 1.1 }}
              >
                {c.name}
              </h3>
              {count > 0 && (
                <span
                  className="label-caps rounded-full px-2.5 py-0.5 text-[9px]"
                  style={{
                    backgroundColor: "#E3B268",
                    color: "#14231B",
                    letterSpacing: "0.2em",
                  }}
                  data-testid={`community-count-${c.slug}`}
                >
                  {count} {count === 1 ? "home" : "homes"} for rent
                </span>
              )}
            </div>
            <p
              className="mt-2 text-[13.5px] leading-[1.5]"
              style={{ color: "rgba(32,30,26,0.7)" }}
            >
              {c.vibe}
            </p>
            {c.bestFor?.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {c.bestFor.slice(0, 3).map((t) => (
                  <li
                    key={t}
                    className="label-caps rounded-full px-2 py-0.5 text-[9px]"
                    style={{
                      backgroundColor: "rgba(20,35,27,0.06)",
                      color: "rgba(20,35,27,0.8)",
                    }}
                  >
                    {t}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="text-right">
            <p
              className="whitespace-nowrap text-[12px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              {minRent ? `From ${money(minRent)}/mo` : c.rent || ""}
            </p>
            <ChevronRight
              size={16}
              className="ml-auto mt-2 opacity-40 transition-opacity group-hover:opacity-80"
              color="#8A6220"
            />
          </div>
        </div>
      </Link>

      {/* Inline listings preview when this community has homes */}
      {listings.length > 0 && (
        <ul className="mt-4 space-y-2 border-t pt-4" style={{ borderColor: "rgba(32,30,26,0.08)" }}>
          {listings.map((p) => {
            const cover = p.hero_images_resolved?.[0];
            const price = p.unit_types
              ?.map((u) => u.rent_from)
              .filter(Boolean)
              .sort((a, b) => a - b)[0];
            return (
              <li key={p.id}>
                <Link
                  to={`/residences/${p.slug}`}
                  className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-white"
                  data-testid={`inline-property-${p.slug}`}
                >
                  {cover && (
                    <div
                      style={{
                        width: 72,
                        height: 56,
                        borderRadius: 8,
                        backgroundImage: `url(${cover})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-display truncate"
                      style={{ fontSize: 15, color: "#201E1A", lineHeight: 1.1 }}
                    >
                      {p.name}
                    </p>
                    <p
                      className="text-[11px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      {p.tagline || "Available now"}
                    </p>
                  </div>
                  {price && (
                    <p
                      className="whitespace-nowrap text-[12px]"
                      style={{ color: "#8A6220" }}
                    >
                      From {money(price)}
                    </p>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
};

const countMinRent = (listings) => {
  const rents = listings.flatMap((p) => (p.unit_types || []).map((u) => u.rent_from)).filter(Boolean);
  return rents.length ? Math.min(...rents) : null;
};

const matchesType = (u, type) => {
  if (type === "all") return true;
  if (type === "1") return u.beds === 1;
  if (type === "2") return u.beds === 2;
  if (type === "3") return u.beds >= 3;
  if (type === "townhome") return /townhome|townhouse|two-storey|two-level|upper|lower/i.test(u.name || "");
  return true;
};

const slugForCommunity = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/\(.+?\)/g, "")
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
